import {
  getOutlinePathsFromImgWithAdaptiveSimplify,
  makePathsBridged,
  pathOffsets,
} from '@marpple/stickerizer';
import {
  CHARM_DEFAULTS,
  ERROR_MESSAGES,
  KEYRING_CHARM_OPTIONS,
  LABS_VECTOR_PRODUCT_TYPES,
  SVG_DEFAULTS,
  VECTOR_PRODUCT_OPTIONS,
} from '../../S/Constant/composite.js';
import { CanvasUtils } from '../../S/Function/canvas.utils.js';
import { FileDownloader, ImageUtils, SizeUtils, SvgUtils } from './composite.utils.js';
import {
  validateMakeCharmImageInput,
  validateMakeKeyringCharmInput,
  validateOptionsByProductType as validateOptions,
  validateMakeMapleCharacterVectorProductInput as validateParams,
} from './composite.validation.js';

const TARGET_VECTOR_PRODUCT_CUT_GENERATE_SIZE = [530, 560, 400, 600, 590, 450, 630, 660, 360];

/**
 * 메이플 캐릭터 벡터 상품을 생성합니다.
 * @async
 * @param {Object} params - 상품 생성에 필요한 파라미터
 * @param {string} params.productType - 상품 타입 (MAPLE_STORY_VECTOR_PRODUCTS의 TYPE 값 중 하나)
 * @param {string} params.size - 상품 크기 (MAPLE_STORY_VECTOR_PRODUCTS 에 맞는 SIZE 값 중 하나)
 * @param {string} params.image_url - 메이플 캐릭터 이미지 URL
 * @param {{x: number, y: number}} params.structurePosition 구조물을 만들기 원하는 x, y 위치
 * @param {boolean} [params.debug=false] - 디버그 모드 활성화 여부 (생성 파일 확인)
 * @returns {Promise<{
 *   canvas: HTMLCanvasElement,
 *   cutlineSvgEl: SVGElement,
 *   structurePoint: {
 *     x: number,
 *     y: number
 *   }
 * }>} 생성된 벡터 상품 정보
 * @throws {Error} 유효하지 않은 입력값이나 이미지 처리 중 오류가 발생한 경우
 */
export async function makeMapleCharacterVectorProduct({
  productType,
  size,
  image_url,
  structurePosition, // = { x: 150, y: 190 } 스탠드 발 위치로 생성되기를 원할 때 주석을 풉니다.
  debug = false,
}) {
  let targetSizeTryIdx = 0;
  let lastError = null;

  while (targetSizeTryIdx < TARGET_VECTOR_PRODUCT_CUT_GENERATE_SIZE.length) {
    try {
      validateParams({ size, productType, image_url });

      const options = VECTOR_PRODUCT_OPTIONS[productType].SIZE[size];
      validateOptions({ productType, options });

      const IS_PIXELATED = true;

      // 2. 이미지 준비
      const {
        canvas: imageCanvasForCutline,
        scaleFactor,
        structurePositionRatio,
      } = await getImageForCutline({
        targetPixelSizeForCutline: TARGET_VECTOR_PRODUCT_CUT_GENERATE_SIZE[targetSizeTryIdx], // 커팅을 위한 이미지 기준 크기
        image_url,
        isPixelated: IS_PIXELATED,
        dpi: options.DPI,
        scale: options.SCALE,
        targetScale: options.TARGET_SCALE,
        maxSizeMm: options.MAX_SIZE_MM,
        cutlineMm: options.CUTLINE_MM,
        structurePosition,
      });

      // 3. 커팅 라인 기본 윤곽선 추출
      const { cutlinePathData } = await createCutlinePathdata({
        canvas: imageCanvasForCutline,
        scaleFactor,
        cutlineMm: options.CUTLINE_MM,
        dpi: options.DPI,
        cutlineYoyo: options.CUTLINE_YOYO,
        binarizeAlphaThreshold: options.BINARIZE_ALPHA_THRESHOLD,
      });

      // target 크기로 scaling
      const scaledPathData = SvgUtils.scalePathData(cutlinePathData, scaleFactor);

      // 4. 벡터 상품 타입에 따라  커팅 라인 구조물을 추가한 최종 cutline SVG 생성
      const cutlineStructure = await createCutlineStructure({
        productType,
        rectSize: SizeUtils.scaleRect({
          rectSize: {
            x: 0,
            y: 0,
            width: imageCanvasForCutline.width,
            height: imageCanvasForCutline.height,
          },
          scaleFactor,
        }),
        printableReadyImageCanvas: imageCanvasForCutline,
        cutlinePathData: scaledPathData,
        options,
        structurePositionRatio,
      });

      const printableImageDesignCanvas = CanvasUtils.resizeCanvas({
        canvas: imageCanvasForCutline,
        scale: scaleFactor,
        isPixelated: IS_PIXELATED,
      });

      // 5. 최종 결과물을 SVG 칼선크기로 trim fitting 되도록 재조정
      const { canvas, cutlineSvgEl, structurePoint } = SizeUtils.makeResultFitted({
        cutlineStructure,
        printableReadyImageCanvas: printableImageDesignCanvas,
        isPixelated: IS_PIXELATED,
      });

      if (debug) {
        const isOnlyCutline = !!debug?.isOnlyCutline;
        const productTypeToName = {
          [LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND]: '스탠드',
          [LABS_VECTOR_PRODUCT_TYPES.KEYRING]: '키링',
        };

        const filename = `${productTypeToName[productType]}-${size}.svg`;

        if (isOnlyCutline) {
          1;
          FileDownloader.downloadSvg(cutlineSvgEl, filename, options.DPI);
        } else {
          const combinedResult = SvgUtils.addImageToSvg(cutlineSvgEl, canvas);
          FileDownloader.downloadSvg(combinedResult, filename, options.DPI);
        }
      }

      return {
        canvas,
        cutlineSvgEl: SvgUtils.injectDpi(cutlineSvgEl, options.DPI),
        structurePoint,
      };
    } catch (error) {
      console.error(
        `Error in makeMapleCharacterVectorProduct (try #${targetSizeTryIdx + 1}/${TARGET_VECTOR_PRODUCT_CUT_GENERATE_SIZE.length})`,
        error,
      );
      lastError = error;
      targetSizeTryIdx++;
    }
  }

  // 모든 시도가 실패한 경우
  throw new Error(`Failed to create vector product after trying all sizes: ${lastError.message}`);
}

/**
 * 제품 유형에 따른 커팅 라인 구조를 생성합니다.
 * @async
 * @param {Object} params - 커팅 라인 구조 생성에 필요한 파라미터
 * @param {string} params.productType - 제품 유형 (LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND 또는 LABS_VECTOR_PRODUCT_TYPES.KEYRING)
 * @param {x: number, y: number, width: number: height: number} params.rectSize
 * @param {string} params.cutlinePathData - SVG 패스 데이터 문자열
 * @param {Object} params.options - 제품별 옵션 정보
 * @param {number} params.options.DPI - 출력 해상도(DPI)
 * @param {Object} [params.options.STAND_SIZE_MM] - 아크릴 스탠드 사이즈(mm) (아크릴 스탠드 타입인 경우)
 * @param {Object} [params.options.STAND_SIZE_MM.GROUND] - 받침대 사이즈
 * @param {number} params.options.STAND_SIZE_MM.GROUND.WIDTH - 받침대 너비(mm)
 * @param {number} params.options.STAND_SIZE_MM.GROUND.HEIGHT - 받침대 높이(mm)
 * @param {Object} params.options.STAND_SIZE_MM.NOTCH - 홈 사이즈
 * @param {number} params.options.STAND_SIZE_MM.NOTCH.WIDTH - 홈 너비(mm)
 * @param {number} params.options.STAND_SIZE_MM.NOTCH.HEIGHT - 홈 높이(mm)
 * @param {number} params.options.STAND_SIZE_MM.NOTCH.ROUND - 홈 라운드(mm)
 * @param {Object} [params.options.RING_SIZE_MM] - 키링 고리 사이즈(mm) (키링 타입인 경우)
 * @param {Object} params.options.RING_SIZE_MM.RADIUS - 고리 반지름
 * @param {number} params.options.RING_SIZE_MM.RADIUS.INNER - 내부 반지름(mm)
 * @param {number} params.options.RING_SIZE_MM.RADIUS.OUTER - 외부 반지름(mm)
 * @param {{x: number, y: number} | null} params.structurePositionRatio - 구조물 위치 비율
 * @param {boolean} [params.debug=false] - 디버그 모드 활성화 여부
 * @returns {Promise<{
 *   cutlineSvgEl: SVGElement,
 *   structurePoint: {
 *     x: number,
 *     y: number
 *   }
 * }>} 생성된 커팅 라인 구조 정보
 * @throws {Error} 유효하지 않은 제품 유형이거나 구조물 생성 실패 시 에러 발생
 */
async function createCutlineStructure({
  productType,
  rectSize,
  cutlinePathData,
  options,
  structurePositionRatio,
  debug,
}) {
  let result;

  switch (productType) {
    case LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND:
      result = await createAcrylicStandCutline({
        rectSize,
        cutlinePathData,
        dpi: options.DPI,
        standSizeMm: options.STAND_SIZE_MM,
        structurePositionRatio,
        debug,
      });
      break;

    case LABS_VECTOR_PRODUCT_TYPES.KEYRING:
      result = await createKeyringCutline({
        rectSize,
        cutlinePathData,
        ringSizeMm: options.RING_SIZE_MM,
        dpi: options.DPI,
        structurePositionRatio,
        debug,
      });
      break;
    default:
      throw new Error(ERROR_MESSAGES.INVALID_PRODUCT_TYPE);
  }

  return { cutlineSvgEl: result.cutlineSvgEl, structurePoint: result.structurePoint };
}

/**
 * 메이플 캐릭터 이미지를 벡터 상품 제작에 적합한 형태로 전처리합니다.
 * @param {Object} params - 이미지 전처리에 필요한 파라미터
 * @param {number} params.targetPixelSizeForCutline - 커팅 라인 생성을 위한 목표 픽셀 크기
 * @param {string} params.image_url - 처리할 메이플 캐릭터 이미지 URL
 * @param {number} params.targetScale - 이미지 스케일 값
 * @param {Object} params.maxSizeMm - 최대 허용 크기(mm) 정보
 * @param {number} params.maxSizeMm.WIDTH - 최대 허용 너비(mm)
 * @param {number} params.maxSizeMm.HEIGHT - 최대 허용 높이(mm)
 * @param {number} params.dpi - 출력 해상도(DPI) 값
 * @param {{x: number, y: number}} params.structurePosition 구조물을 만들기 원하는 x, y 위치
 * @param {boolean} [params.isPixelated] - 픽셀화 스타일 적용 여부
 * @param {boolean} [params.debug=false] - 디버그 모드 활성화 여부
 * @returns {Promise<{canvas: HTMLCanvasElement, scaleFactor: number}>} 전처리가 완료된 결과 객체
 * @throws {Error} 이미지 처리 중 오류 발생 시
 */
async function getImageForCutline({
  targetPixelSizeForCutline,
  image_url,
  targetScale,
  maxSizeMm,
  dpi,
  isPixelated,
  structurePosition,
  debug = false,
}) {
  const originalCanvas = await CanvasUtils.makeCanvasByUrl({ url: image_url, isPixelated });

  const { finalScale, originalDesignWidthPx, originalDesignHeightPx } = SizeUtils.calculateScaleWithSizeLimit(
    {
      originalCanvas,
      targetScale,
      maxSizeMm,
      dpi,
    },
  );

  const cutlineScale = targetPixelSizeForCutline / Math.max(originalDesignWidthPx, originalDesignHeightPx);

  // 주의! 🚨 스케일 리사이징 후 -> 투명 여백 제거 순서 보장 필요
  // 2. 픽셀화된 스타일로 일정 스케일 리사이징
  const resizedCanvasForCutline = CanvasUtils.resizeCanvas({
    canvas: originalCanvas,
    scale: cutlineScale,
    isPixelated,
  });

  // 3. 투명 여백 제거
  const { canvas: trimPaddedCanvas, trimmedLength } = CanvasUtils.trimPadding({
    canvas: resizedCanvasForCutline,
    padding: 100, // 적당히 고정값
    isPixelated,
  });

  // 디버그 모드일 경우 이미지 다운로드
  if (debug) {
    FileDownloader.downloadCanvas(trimPaddedCanvas, 'paddedCanvas.png', dpi);
  }

  return {
    canvas: trimPaddedCanvas,
    scaleFactor: finalScale / cutlineScale,
    structurePositionRatio: structurePosition
      ? calculateStructurePointRatio({
          structurePosition,
          originalCanvas,
          trimmedLength,
          trimPaddedCanvas,
          resizedCanvasForCutline,
        })
      : null,
  };
}

function calculateStructurePointRatio({
  structurePosition,
  originalCanvas,
  trimmedLength,
  trimPaddedCanvas,
  resizedCanvasForCutline,
}) {
  const originalStructurePointRatio = {
    x: structurePosition.x / originalCanvas.width,
    y: structurePosition.y / originalCanvas.height,
  };
  const resizedStructurePositionPx = {
    x: originalStructurePointRatio.x * resizedCanvasForCutline.width,
    y: originalStructurePointRatio.y * resizedCanvasForCutline.height,
  };

  const trimmedStructurePositionPx = {
    x: resizedStructurePositionPx.x - trimmedLength.left,
    y: resizedStructurePositionPx.y - trimmedLength.top,
  };

  return {
    x: trimmedStructurePositionPx.x / trimPaddedCanvas.width,
    y: trimmedStructurePositionPx.y / trimPaddedCanvas.height,
  };
}

/**
 * 키링 상품의 커팅 라인을 생성합니다.
 * @param {{x: number, y: number, width: number, height: number}} rectSize - 아트보드 크기 정보
 * @param {string} cutlinePathData - 기본 커팅 라인 SVG 경로 데이터
 * @param {RADIUS: {INNER: number, OUTER: number}} ringSizeMm - 키링 크기 정보 (mm)
 * @param {number} dpi - DPI 값
 * @param {boolean} [debug=false] - 디버그 모드 활성화 여부
 * @returns {Promise<{cutlineSvgEl: SVGElement, structurePoint: {x: number, y: number}}>} 생성된 키링 커팅 라인 정보
 * @throws {Error} 키링 생성 중 오류 발생 시
 */
export async function createKeyringCutline({ rectSize, cutlinePathData, ringSizeMm, dpi, debug = false }) {
  try {
    const KeyringFactory = (await import('@marpple/keyring_hook/src/index.js')).default;

    if (KeyringFactory == null) {
      throw new Error(`KeyringFactory is not available`);
    }

    const {
      RADIUS: { INNER, OUTER },
    } = ringSizeMm;

    const keyringHook = new KeyringFactory({
      art_board_size: rectSize,
      path_data: cutlinePathData,
      radius: {
        inner: ImageUtils.mmToPx(INNER, dpi),
        outer: ImageUtils.mmToPx(OUTER, dpi),
      },
      default_positioning: 'CENTER', // 키링 홀을 항상 외곽선 바깥쪽에 배치
    });

    // 키링 홀과 외곽선을 하나의 경로로 통합
    const hookUnitedCutlinePathdata = keyringHook.getPathDataHookUnited();

    // 최종 SVG 엘리먼트 생성
    const cutlineSvgEl = SvgUtils.createCutlineSvg({
      pathData: hookUnitedCutlinePathdata,
      color: SVG_DEFAULTS.COLOR,
      strokeWidth: SVG_DEFAULTS.STROKE_WIDTH,
      width: rectSize.width,
      height: rectSize.height,
    });

    if (debug) {
      FileDownloader.downloadSvg(cutlineSvgEl, 'cutline-keyring.svg', dpi);
    }

    const keyringHookCenter = keyringHook.last_center_position;

    return { cutlineSvgEl, structurePoint: keyringHookCenter };
  } catch (error) {
    throw new Error(`createKeyringCutline: ${error.message}`);
  }
}

/**
 * 아크릴 스탠드 상품의 커팅 라인을 생성합니다.
 * @param {Object} params - 아크릴 스탠드 커팅 라인 생성에 필요한 파라미터
 * @param {Object} params.rectSize - 아트보드 크기 정보
 * @param {string} params.cutlinePathData - 기본 커팅 라인 SVG 경로 데이터
 * @param {Object} params.options - 아크릴 스탠드 생성 옵션
 * @param {number} params.dpi - DPI 값
 * @param {Object} params.standSizeMm - 스탠드 크기 정보 (mm)
 * @param {{x: number, y: number} | null} params.structurePositionRatio - 구조물 위치 비율
 * @param {boolean} [params.debug=false] - 디버그 모드 활성화
 * @returns {Promise<{cutlineSvgEl: SVGElement, structurePoint: {x: number, y: number}}>} 생성된 아크릴 스탠드 커팅 라인 정보
 * @throws {Error} 아크릴 스탠드 생성 중 오류 발생 시
 */
async function createAcrylicStandCutline({
  rectSize,
  cutlinePathData,
  dpi,
  standSizeMm,
  structurePositionRatio,
  debug = false,
}) {
  try {
    const acrylicStandFactory = (await import('@marpple/acryl-stand')).default;

    const { GROUND, NOTCH } = standSizeMm;

    const result = acrylicStandFactory({
      svg_artboard_rect: rectSize,
      pathdata: cutlinePathData,
      size: {
        ground: {
          width: ImageUtils.mmToPx(GROUND.WIDTH, dpi),
          height: ImageUtils.mmToPx(GROUND.HEIGHT, dpi),
        },
        notch: {
          width: ImageUtils.mmToPx(NOTCH.WIDTH, dpi),
          height: ImageUtils.mmToPx(NOTCH.HEIGHT, dpi),
          round: ImageUtils.mmToPx(NOTCH.ROUND, dpi),
        },
      },
      initial_position: `CENTER`,
    });

    if (!result.ok) {
      throw new Error(`${result.value.code} - ${result.value.message}`);
    }

    const acrylicStand = result.value;

    let unitedAndStandPathData;
    if (structurePositionRatio == null) {
      unitedAndStandPathData = acrylicStand.getUnitedPathdata({}).value;
    } else {
      const groundPosX = structurePositionRatio.x * rectSize.width;
      unitedAndStandPathData = acrylicStand.getUnitedPathdata({
        x: groundPosX,
      }).value;
    }

    const groundMidPointResponse = acrylicStand.getGroundMidPoint();
    if (!groundMidPointResponse.ok) {
      throw new Error(`${groundMidPointResponse.value.code} - ${groundMidPointResponse.value.message}`);
    }
    const groundMidPoint = groundMidPointResponse.value;

    const cutlineSvgEl = SvgUtils.createCutlineSvg({
      pathData: unitedAndStandPathData,
      color: SVG_DEFAULTS.COLOR,
      strokeWidth: SVG_DEFAULTS.STROKE_WIDTH,
      width: rectSize.width,
      height: rectSize.height,
    });

    if (debug) {
      FileDownloader.downloadSvg(cutlineSvgEl, 'cutline-acrylic-stand.svg', dpi);
    }

    return { cutlineSvgEl, structurePoint: groundMidPoint };
  } catch (error) {
    throw new Error(`createAcrylicStandCutline: ${error.message}`);
  }
}

/**
 * 이미지로부터 커팅 라인 경로 데이터를 생성합니다.
 * @async
 * @param {Object} params - 커팅 라인 생성에 필요한 파라미터
 * @param {HTMLCanvasElement} params.canvas - 이미지가 포함된 캔버스 엘리먼트
 * @param {number} params.scaleFactor - 이미지 스케일 조정 비율
 * @param {Object} params.cutlineMm - 커팅 라인 설정값 (mm 단위)
 * @param {number} params.cutlineMm.SMOOTHNESS - 곡선 스무딩 정도 (0~100)
 * @param {number} params.cutlineMm.OFFSET - 외곽선 확장 거리 (mm)
 * @param {number} params.dpi - 출력 해상도(DPI) 값
 * @param {number} params.cutlineYoyo - 커팅 라인 요요 값 (칼선을 늘렸다가 줄여서 커팅 라인을 조정하는 경우) (mm)
 * @param {number} params.binarizeAlphaThreshold - 알파 채널 이진화 임계값 (0~1)
 * @param {boolean} [params.debug=false] - 디버그 모드 활성화 여부
 * @returns {Promise<{cutlinePathData: string}>} 생성된 커팅 라인 정보
 * @throws {Error} 커팅 라인 생성 중 오류 발생 시
 */
async function createCutlinePathdata({
  canvas,
  scaleFactor,
  cutlineMm,
  dpi,
  cutlineYoyo,
  binarizeAlphaThreshold,
  debug = false,
}) {
  // 1. 이미지 외곽선 경로 추출
  const fit_cutting_line = await getOutlinePathsFromImgWithAdaptiveSimplify(
    null,
    null,
    cutlineMm.SMOOTHNESS,
    await ImageUtils.createImageElFromCanvas({ canvas }),
    binarizeAlphaThreshold,
  );

  // 2. 분리된 칼선 경로들을 하나로 연결
  const base_cutting_line = await makePathsBridged(fit_cutting_line.paper_obj);

  // 3. 지정된 오프셋만큼 외곽선 확장 (scaleFactor 를 고려하여 오프셋 값을 조정)
  let cutline;
  const offsetMm = Math.min(cutlineMm.OFFSET / scaleFactor, 0.1);

  // cutlineYoyo 가 양수일 경우 커팅 라인을 늘렸다가 줄여서 커팅 라인을 조정
  if (cutlineYoyo > 0) {
    const tempCutline = await pathOffsets(
      base_cutting_line.paper_obj,
      ImageUtils.mmToPx(offsetMm + cutlineYoyo, dpi),
    );
    cutline = await pathOffsets(tempCutline.paper_obj, -ImageUtils.mmToPx(cutlineYoyo, dpi));
  } else {
    // cutlineYoyo 가 0 이하일 경우 한번만 offset 적용
    cutline = await pathOffsets(base_cutting_line.paper_obj, ImageUtils.mmToPx(offsetMm, dpi));
  }

  if (debug) {
    [{ name: 'added', cutline }].forEach(({ name, cutline }) => {
      const svg_el = SvgUtils.createCutlineSvg({
        pathData: cutline.path_data,
        color: SVG_DEFAULTS.COLOR,
        strokeWidth: SVG_DEFAULTS.STROKE_WIDTH,
        width: canvas.width,
        height: canvas.height,
      });

      FileDownloader.downloadSvg(svg_el, `cutline_${name}.svg`, dpi);
    });
  }

  return { cutlinePathData: cutline.path_data };
}

/**
 * 키링 참 상품을 생성합니다.
 * @param {Object} params - 키링 참 생성에 필요한 파라미터
 * @param {string} [params.text=''] - 렌더링할 텍스트
 * @param {string} [params.fontColor=CHARM_DEFAULTS.FONT.COLOR] - 폰트 색상 (CSS 색상 값)
 * @param {number} [params.fontSize=CHARM_DEFAULTS.FONT.SIZE] - 폰트 크기 (px)
 * @param {number} [params.tradeMarkgapPx=CHARM_DEFAULTS.TRADEMARK.GAP_PX] - 텍스트와 상표 이미지 사이의 간격 (px)
 * @param {string} [params.padding=CHARM_DEFAULTS.FONT.PADDING] - CSS 스타일 패딩 값 (예: "20 0")
 * @param {string} [params.tradeMarkImageUrl=CHARM_DEFAULTS.TRADEMARK.IMAGE_URL] - 상표 이미지 URL
 * @param {Object} [params.cutlineMargin=CHARM_DEFAULTS.CUTLINE.MARGIN] - 커팅 라인 마진 설정
 * @param {number} [params.cutlineMargin.top=CHARM_DEFAULTS.CUTLINE.MARGIN.TOP] - 상단 마진
 * @param {number} [params.cutlineMargin.right=CHARM_DEFAULTS.CUTLINE.MARGIN.RIGHT] - 우측 마진
 * @param {number} [params.cutlineMargin.bottom=CHARM_DEFAULTS.CUTLINE.MARGIN.BOTTOM] - 하단 마진
 * @param {number} [params.cutlineMargin.left=CHARM_DEFAULTS.CUTLINE.MARGIN.LEFT] - 좌측 마진
 * @param {number} [params.cutlineRectRound=CHARM_DEFAULTS.CUTLINE.ROUND] - 커팅 라인 모서리 라운드 값 (px)
 * @param {number} [params.ringConnectionRoundPx=CHARM_DEFAULTS.CUTLINE.RING_CONNECTION_ROUND_PX] - 링 연결부 라운드 값 (px)
 * @param {Object} [params.backgroundImageUrls=CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS] - 배경 이미지 URL
 * @param {string} [params.backgroundImageUrls.left=CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.LEFT] - 왼쪽 배경 이미지 URL
 * @param {string} [params.backgroundImageUrls.mid=CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.MID] - 중앙 배경 이미지 URL
 * @param {string} [params.backgroundImageUrls.right=CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.RIGHT] - 오른쪽 배경 이미지 URL
 * @param {boolean} [params.debug=false] - 디버깅 목적의 생성 파일 다운로드
 * @returns {Promise<{canvas: HTMLCanvasElement, cutlineSvgEl: SVGElement, structurePoint: {x: number, y: number}}>} 생성된 키링 참 상품 정보
 * @throws {Error} 유효하지 않은 입력값이 있을 경우 또는 생성 중 오류 발생 시
 */
export async function makeKeyringCharm({
  text = '',
  fontUrl = CHARM_DEFAULTS.FONT.FAMILY,
  fontColor = CHARM_DEFAULTS.FONT.COLOR,
  fontSize = CHARM_DEFAULTS.FONT.SIZE,
  tradeMarkgapPx = CHARM_DEFAULTS.TRADEMARK.GAP_PX,
  padding = CHARM_DEFAULTS.FONT.PADDING,
  tradeMarkImageUrl = CHARM_DEFAULTS.TRADEMARK.IMAGE_URL,
  cutlineMargin = {
    top: CHARM_DEFAULTS.CUTLINE.MARGIN.TOP,
    right: CHARM_DEFAULTS.CUTLINE.MARGIN.RIGHT,
    bottom: CHARM_DEFAULTS.CUTLINE.MARGIN.BOTTOM,
    left: CHARM_DEFAULTS.CUTLINE.MARGIN.LEFT,
  },
  cutlineRectRound = CHARM_DEFAULTS.CUTLINE.ROUND,
  ringConnectionRoundPx = CHARM_DEFAULTS.CUTLINE.RING_CONNECTION_ROUND_PX,
  backgroundImageUrls = {
    left: CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.LEFT,
    mid: CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.MID,
    right: CHARM_DEFAULTS.BACKGROUND.IMAGE_URLS.RIGHT,
  },
  debug = false,
}) {
  if (text == null) {
    throw new Error('text is required');
  }
  // 입력값 검증
  validateMakeKeyringCharmInput({
    text,
    fontColor,
    fontSize,
    tradeMarkgapPx,
    padding,
    tradeMarkImageUrl,
    cutlineMargin,
    cutlineRectRound,
    ringConnectionRoundPx,
    backgroundImageUrls,
  });

  const strokeHalfWidthPx = Number(SVG_DEFAULTS.STROKE_WIDTH) / 2;
  const keyringProductOption = KEYRING_CHARM_OPTIONS;

  const topMargin =
    ImageUtils.mmToPx(keyringProductOption.RING_SIZE_MM.RADIUS.INNER * 2) + 18 + ringConnectionRoundPx;

  const { canvas: designCanvas, rect: designRect } = await makeCharmTextBallon({
    backgroundImageUrls,
    fontUrl,
    padding,
    text,
    textProps: {
      fontColor,
      fontSize,
      fontUrl,
    },
    textTradeMarkgapPx: tradeMarkgapPx,
    tradeMarkImageUrl,
    margin: {
      top: topMargin + cutlineMargin.top + strokeHalfWidthPx,
      bottom: cutlineMargin.bottom + strokeHalfWidthPx,
      left: cutlineMargin.left + strokeHalfWidthPx,
      right: cutlineMargin.right + strokeHalfWidthPx,
    },
  });

  const { svgEl, ringCenter } = await makeCharmCutlineSvg({
    cutlineMargin,
    rectRound: cutlineRectRound,
    ringConnectionRoundPx,
    svgRect: { x: 0, y: 0, width: designCanvas.width, height: designCanvas.height },
    cutlineRect: designRect,
    ringSizeMm: keyringProductOption.RING_SIZE_MM,
    dpi: keyringProductOption.DPI,
  });

  if (debug) {
    FileDownloader.downloadSvg(svgEl, 'cutline.svg', keyringProductOption.DPI);
    FileDownloader.downloadCanvas(designCanvas, 'design.png', keyringProductOption.DPI);
  }

  return {
    canvas: designCanvas,
    cutlineSvgEl: SvgUtils.injectDpi(svgEl, keyringProductOption.DPI),
    structurePoint: ringCenter,
  };
}

/**
 * 텍스트가 포함된 참 이미지를 생성합니다.
 * @param {Object} params - 참 이미지 생성에 필요한 파라미터
 * @param {Object} params.backgroundImageUrls - 배경 이미지 URL (left, mid, right)
 * @param {string} params.fontUrl - 폰트 파일 URL
 * @param {string} params.tradeMarkImageUrl - 상표 이미지 URL
 * @param {number} params.gap - 배경과 하단의 상표 이미지 간의 간격
 * @param {Object} params.margin - 외부 여백 설정
 * @param {number} [params.margin.top=0] - 상단 외부 여백
 * @param {number} [params.margin.right=0] - 우측 외부 여백
 * @param {number} [params.margin.bottom=0] - 하단 외부 여백
 * @param {number} [params.margin.left=0] - 좌측 외부 여백
 * @param {string} params.padding - CSS 스타일 패딩 값 (예: "5 10")
 * @param {string} params.text - 렌더링할 텍스트
 * @param {Object} params.textProps - 텍스트 스타일 속성
 * @returns {Promise<{canvas: HTMLCanvasElement, rect: {x: number, y: number, width: number, height: number}}>} 생성된 참 이미지 캔버스와 위치 정보
 */
async function makeCharmTextBallon({
  backgroundImageUrls,
  fontUrl,
  tradeMarkImageUrl,
  textTradeMarkgapPx = 0,
  margin = {},
  padding,
  text,
  textProps,
  debug = false,
}) {
  // 입력값 검증
  validateMakeCharmImageInput({
    backgroundImageUrls,
    fontUrl,
    padding,
    text,
    textProps,
  });

  // 마진 값이 undefined인 경우 0으로 처리
  const margins = {
    top: margin.top ?? 0,
    right: margin.right ?? 0,
    bottom: margin.bottom ?? 0,
    left: margin.left ?? 0,
  };

  // 1. 폰트 로드
  const fontFamily = 'CustomFont';
  const fontFace = new FontFace(fontFamily, `url(${fontUrl})`);
  await fontFace.load();
  document.fonts.add(fontFace);

  // 2. 패딩 파싱
  const paddingValues = padding.split(' ').map(Number);
  const [paddingY, paddingX] =
    paddingValues.length === 1 ? [paddingValues[0], paddingValues[0]] : paddingValues;

  // 3. 임시 캔버스에 텍스트 렌더링하여 크기 측정
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d', { willReadFrequently: true });
  measureCtx.imageSmoothingEnabled = false;

  // 폰트 크기를 2의 배수로 조정하여 픽셀 정렬 보장
  const fontSize = Math.round(textProps.fontSize / 2) * 2;
  measureCtx.font = `${fontSize}px ${fontFamily}`;

  const textMetrics = measureCtx.measureText(text);
  // 텍스트 크기를 2의 배수로 반올림
  const textWidth = Math.ceil(textMetrics.width / 2) * 2;
  const textHeight = fontSize;

  // 4. 이미지 로드
  const loadImage = ImageUtils.createImageElFromUrl;
  const [leftImg, midImg, rightImg, tradeMarkImg] = await Promise.all([
    loadImage(backgroundImageUrls.left),
    loadImage(backgroundImageUrls.mid),
    loadImage(backgroundImageUrls.right),
    loadImage(tradeMarkImageUrl),
  ]);

  // 상표 이미지의 실제 렌더링 크기 계산 (2배 크기에서 실제 크기로)
  const tradeMarkWidth = tradeMarkImg.width / 2;
  const tradeMarkHeight = tradeMarkImg.height / 2;

  // 5. 배경 이미지 크기 계산 (모든 크기를 2의 배수로 조정)
  const bgHeight = Math.ceil((textHeight + paddingY * 2) / 2) * 2;
  const aspectRatio = {
    left: leftImg.width / leftImg.height,
    right: rightImg.width / rightImg.height,
  };

  const sideWidth = Math.ceil((bgHeight * aspectRatio.left) / 2) * 2;
  const bgWidth = Math.ceil((textWidth + paddingX * 2 + sideWidth * 2) / 2) * 2;

  // 전체 캔버스 크기 계산 (배경 + gap + 상표 + 마진)
  const contentWidth = Math.max(bgWidth, tradeMarkWidth);
  const contentHeight = bgHeight + textTradeMarkgapPx + tradeMarkHeight;

  const totalWidth = contentWidth + margins.left + margins.right;
  const totalHeight = contentHeight + margins.top + margins.bottom;

  // 6. 최종 캔버스 생성
  const canvas = document.createElement('canvas');
  canvas.width = totalWidth;
  canvas.height = totalHeight;
  const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });

  // 배경 이미지의 x 위치 계산 (중앙 정렬)
  const bgX = margins.left + (contentWidth - bgWidth) / 2;
  const bgY = margins.top;

  // 7. 배경 이미지 그리기
  const OVERLAP = 1;

  // 왼쪽 배경
  CanvasUtils.drawPixelatedImage({
    ctx,
    image: leftImg,
    x: bgX,
    y: bgY,
    width: sideWidth,
    height: bgHeight,
    isPixelated: true,
    overlap: OVERLAP,
  });

  // 중앙 배경
  CanvasUtils.drawRepeatingImage({
    ctx,
    image: midImg,
    x: bgX + sideWidth - OVERLAP,
    y: bgY,
    width: bgWidth - sideWidth * 2 + OVERLAP * 2,
    height: bgHeight,
    isPixelated: true,
    overlap: OVERLAP,
  });

  // 오른쪽 배경
  CanvasUtils.drawPixelatedImage({
    ctx,
    image: rightImg,
    x: bgX + bgWidth - sideWidth - OVERLAP,
    y: bgY,
    width: sideWidth,
    height: bgHeight,
    isPixelated: true,
    overlap: OVERLAP,
  });

  // 8. 텍스트 렌더링
  // 텍스트 위치 계산 (배경 영역 내 중앙)
  const textX = bgWidth / 2;
  const textY = bgHeight / 2;

  // 텍스트 렌더링을 위한 임시 캔버스 생성 (2배 해상도)
  const tempCanvas = document.createElement('canvas');
  const scale = 2;
  tempCanvas.width = bgWidth * scale;
  tempCanvas.height = bgHeight * scale;
  const tempCtx = tempCanvas.getContext('2d', { alpha: true, willReadFrequently: true });

  // 렌더링 최적화 설정
  tempCtx.imageSmoothingEnabled = false;
  tempCtx.textRendering = 'geometricPrecision';
  tempCtx.fontKerning = 'none';

  // 고해상도로 텍스트 렌더링
  tempCtx.scale(scale, scale);
  tempCtx.font = `${fontSize}px ${fontFamily}`;
  tempCtx.fillStyle = textProps.fontColor;
  tempCtx.textBaseline = 'middle';
  tempCtx.textAlign = 'center';
  tempCtx.fillText(text, textX, textY);

  // 고해상도 텍스트를 원본 크기로 축소 (pixelated)
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tempCanvas, 0, 0, bgWidth * scale, bgHeight * scale, bgX, bgY, bgWidth, bgHeight);

  // 9. 상표 이미지 그리기
  const tradeMarkX = margins.left + (contentWidth - tradeMarkWidth) / 2;
  const tradeMarkY = margins.top + bgHeight + textTradeMarkgapPx;

  // 상표 이미지는 선명하게 그리기
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(tradeMarkImg, tradeMarkX, tradeMarkY, tradeMarkWidth, tradeMarkHeight);
  ctx.restore();

  if (debug) {
    const DPI = 300;
    FileDownloader.downloadCanvas(canvas, 'charm.png', DPI);
  }

  // 10. 위치 정보 계산
  const rect = {
    x: margins.left,
    y: margins.top,
    width: contentWidth,
    height: contentHeight,
  };

  return { canvas, rect };
}

async function makeCharmCutlineSvg({
  svgRect,
  cutlineRect,
  cutlineMargin,
  rectRound,
  ringSizeMm,
  dpi,
  ringConnectionRoundPx = 8, // 링 연결부의 round 크기 (기본값 8px)
  debug = false,
}) {
  try {
    // SVG 요소 생성
    const svgEl = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'svg');
    svgEl.setAttribute('width', svgRect.width);
    svgEl.setAttribute('height', svgRect.height);
    svgEl.setAttribute('viewBox', `${svgRect.x} ${svgRect.y} ${svgRect.width} ${svgRect.height}`);

    // margin이 없는 경우 기본값 설정
    const margins = {
      top: cutlineMargin?.top ?? 0,
      right: cutlineMargin?.right ?? 0,
      bottom: cutlineMargin?.bottom ?? 0,
      left: cutlineMargin?.left ?? 0,
    };

    // path 위치 계산 (margin 적용)
    const pathX = cutlineRect.x - margins.left;
    const pathY = cutlineRect.y - margins.top;
    const pathWidth = cutlineRect.width + margins.left + margins.right;
    const pathHeight = cutlineRect.height + margins.top + margins.bottom;

    // 링 크기를 px로 변환
    const innerRadius = ImageUtils.mmToPx(ringSizeMm.RADIUS.INNER, dpi);
    const outerRadius = ImageUtils.mmToPx(ringSizeMm.RADIUS.OUTER, dpi);

    // 링의 중심점 계산 (사각형 상단 중심에서 innerRadius만큼 위로)
    const centerX = pathX + pathWidth / 2;
    const ringCenterY = pathY - innerRadius;

    // compound path를 위한 g 요소 생성
    const gEl = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'g');
    gEl.setAttribute('fill', 'none');
    gEl.setAttribute('stroke', SVG_DEFAULTS.COLOR);
    gEl.setAttribute('stroke-width', SVG_DEFAULTS.STROKE_WIDTH);

    // 1. 사각형과 outer ring arc를 포함하는 path 생성
    const mainPathData = [
      // 시작점 (좌측 상단 모서리 시작)
      `M ${pathX + rectRound} ${pathY}`,

      // 상단 왼쪽 가로선 (링 시작점까지)
      `H ${centerX - outerRadius - ringConnectionRoundPx}`,

      // 90도 arc로 outer ring arc 시작점으로 부드럽게 연결 (connectionRound radius, 4분원)
      `A ${ringConnectionRoundPx} ${ringConnectionRoundPx} 0 0 0 ${centerX - outerRadius} ${pathY - ringConnectionRoundPx}`,

      // outer ring arc 시작 (반시계 방향)
      `A ${outerRadius} ${outerRadius} 0 1 1 ${centerX + outerRadius} ${pathY - ringConnectionRoundPx}`,

      // 90도 arc로 사각형으로 부드럽게 연결 (connectionRound radius, 4분원)
      `A ${ringConnectionRoundPx} ${ringConnectionRoundPx} 0 0 0 ${centerX + outerRadius + ringConnectionRoundPx} ${pathY}`,

      // 상단 오른쪽 가로선
      `H ${pathX + pathWidth - rectRound}`,

      // 우측 상단 모서리
      `Q ${pathX + pathWidth} ${pathY} ${pathX + pathWidth} ${pathY + rectRound}`,

      // 우측 세로선
      `V ${pathY + pathHeight - rectRound}`,

      // 우측 하단 모서리
      `Q ${pathX + pathWidth} ${pathY + pathHeight} ${pathX + pathWidth - rectRound} ${pathY + pathHeight}`,

      // 하단 가로선
      `H ${pathX + rectRound}`,

      // 좌측 하단 모서리
      `Q ${pathX} ${pathY + pathHeight} ${pathX} ${pathY + pathHeight - rectRound}`,

      // 좌측 세로선
      `V ${pathY + rectRound}`,

      // 좌측 상단 모서리
      `Q ${pathX} ${pathY} ${pathX + rectRound} ${pathY}`,

      // 경로 닫기
      'Z',
    ].join(' ');

    const mainPathEl = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'path');
    mainPathEl.setAttribute('d', mainPathData);

    // 2. inner ring circle path 생성
    const innerCirclePathData = [
      `M ${centerX + innerRadius} ${ringCenterY}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${centerX - innerRadius} ${ringCenterY}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${centerX + innerRadius} ${ringCenterY}`,
      'Z',
    ].join(' ');

    const innerCirclePathEl = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'path');
    innerCirclePathEl.setAttribute('d', innerCirclePathData);

    // path 요소들을 g 요소에 추가
    gEl.appendChild(mainPathEl);
    gEl.appendChild(innerCirclePathEl);

    // g 요소를 SVG에 추가
    svgEl.appendChild(gEl);

    if (debug) {
      FileDownloader.downloadSvg(svgEl, 'charm_cutline.svg', dpi);
    }

    return {
      svgEl,
      pathData: [mainPathData, innerCirclePathData],
      ringCenter: { x: centerX, y: ringCenterY },
    };
  } catch (error) {
    throw new Error(`Failed to create charm cutline SVG: ${error.message}`);
  }
}
